<script setup lang="ts">
import {
  computed, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { useRouter } from 'vue-router';
import DataTable, { DataTableRowClickEvent, DataTableFilterEvent } from 'primevue/datatable';
import type { DataTableExportFunctionOptions, DataTableFilterMeta, DataTableFilterMetaData } from 'primevue/datatable';
import { FilterMatchMode } from '@primevue/core/api';
import Column from 'primevue/column';
import Button from 'primevue/button';
import ToggleSwitch from 'primevue/toggleswitch';
import Badge from 'primevue/badge';
import Avatar from 'primevue/avatar';
import InputText from 'primevue/inputtext';
import Tag from 'primevue/tag';
import Select from 'primevue/select';
import CalendarTimesIcon from '@primeicons/vue/calendar-times';
import CircleFillIcon from '@primeicons/vue/circle-fill';
import FileExportIcon from '@primeicons/vue/file-export';
import FilterSlashIcon from '@primeicons/vue/filter-slash';

import AppContainer from '@apparatix/library/AppContainer.vue';
import FormSkeleton from '@apparatix/Components/FormSkeleton.vue';
import ColumnDisplaySelector from '@apparatix/library/ColumnDisplaySelector.vue';
import MultiSelect from '@apparatix/library/MultiSelect.vue';
import HeightOptimizer from './HeightOptimizer.vue';
import ExpirationActionDialog from './ExpirationActionDialog.vue';
import {
  getExpirationStatusSeverity,
  useBuildDefaultColumns,
  useColumnConfig,
  useExpirations,
  useKeepAliveActive,
  useLocalStorage,
  useOpportunities,
  useStringFunctions,
} from '@apparatix/composables';
import { CustomEventNames } from '@apparatix/constants/CustomEventNames';
import type { Column as ColumnConfig } from '@apparatix/types';
import type { ExpirationRow, ExpirationSettings, ExpirationStatus } from '@apparatix/types/Expirations';

const {
  createRenewal,
  filterExpirations,
  ignoreSubcontract,
  isActing,
  setNotRenewing,
  updateNotes,
} = useExpirations();
const { loadOpportunityOptions } = useOpportunities();
const { formatAsCurrency } = useStringFunctions();
const { getItem: getStoredItem, setItem: setStoredItem } = useLocalStorage();
const router = useRouter();
const { isKeepAliveActive } = useKeepAliveActive();

const defaultFilters: DataTableFilterMeta = {
  accountName: { value: null, matchMode: FilterMatchMode.CONTAINS },
  contractId: { value: null, matchMode: FilterMatchMode.CONTAINS },
  endDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
  zone: { value: null, matchMode: FilterMatchMode.IN },
  periods: { value: null, matchMode: FilterMatchMode.EQUALS },
  monthly: { value: null, matchMode: FilterMatchMode.EQUALS },
  total: { value: null, matchMode: FilterMatchMode.EQUALS },
  aeName: { value: null, matchMode: FilterMatchMode.IN },
  firstRight: { value: null, matchMode: FilterMatchMode.IN },
  office: { value: null, matchMode: FilterMatchMode.IN },
  boards: { value: null, matchMode: FilterMatchMode.CONTAINS },
  status: { value: null, matchMode: FilterMatchMode.IN },
  isSubcontract: { value: null, matchMode: FilterMatchMode.IN },
  notes: { value: null, matchMode: FilterMatchMode.CONTAINS },
};
const availableStates = ['Expiring', 'All'];
const renewalStatuses: ExpirationStatus[] = ['Renewing', 'Renewed', 'Renewal Lost'];
const firstRightOptions = [{ label: 'Yes', value: true }, { label: 'No', value: false }];
const numericFilterMatchModeOptions = [
  { label: 'Equals', value: FilterMatchMode.EQUALS },
  { label: 'Less Than', value: FilterMatchMode.LESS_THAN },
  { label: 'Greater Than', value: FilterMatchMode.GREATER_THAN },
  { label: 'Between', value: FilterMatchMode.BETWEEN },
];
const dateFilterMatchModeOptions = [
  { label: 'Contains', value: FilterMatchMode.CONTAINS },
  { label: 'Less Than', value: FilterMatchMode.LESS_THAN },
  { label: 'Greater Than', value: FilterMatchMode.GREATER_THAN },
  { label: 'Between', value: FilterMatchMode.BETWEEN },
];
const numericFilterFields = ['periods', 'monthly', 'total'] as const;
const multiModeFilterFields = [...numericFilterFields, 'endDate'] as const;
const delay = 250;

const dt = ref();
const expirations = ref<ExpirationRow[]>([]);
const settings = ref<ExpirationSettings>();
const isLoading = ref(false);
const currentState = ref<'Expiring' | 'All'>('Expiring');
const showFiltersKey = 'crm:expirations:show-filters';
const showFilters = ref(getStoredItem<boolean>(showFiltersKey) ?? true);
const filteredExpirationLength = ref<number>();
const selectedExpiration = ref<ExpirationRow>();
const tableFilters = ref<DataTableFilterMeta>({ ...defaultFilters });
const previousTableFilters = ref<DataTableFilterMeta>();
const filteredRows = ref<ExpirationRow[]>([]);
const showActionDialog = ref(false);
const clickTimeout = ref<number>();
const editingNotesKey = ref<string>(null);
let tempNotes = '';

// useColumnConfig needs `dt` above to already exist, so this pair is initialized here instead of with the other composables
const defaultColumns = useBuildDefaultColumns([
  ['accountName', 'Account'],
  ['contractId', 'Contract Id'],
  ['endDate', 'End Date'],
  ['zone', 'Zone'],
  ['periods', 'Periods'],
  ['monthly', 'Monthly', false],
  ['total', 'Total'],
  ['aeName', 'AE'],
  ['firstRight', 'First Right'],
  ['office', 'Office'],
  ['boards', 'Boards'],
  ['status', 'Status'],
  ['isSubcontract', 'Subcontract'],
  ['notes', 'Notes'],
]);

const {
  handleColumnOrderChange,
  handleVisibleColumnChange,
  visibleColumns,
  columnOrder,
} = useColumnConfig({
  visibleItemsKey: 'crm:expirations:visible-columns',
  columnOrderKey: 'crm:expirations:column-order',
  ref: dt,
  defaultColumns: defaultColumns,
});

const isFilterChanged = computed<boolean>(() => JSON.stringify(tableFilters.value) !== JSON.stringify(defaultFilters));

const columnSettingsGates = computed<Record<string, boolean>>(() => ({
  boards: settings.value?.showBoards ?? false,
  isSubcontract: settings.value?.showSubcontracts ?? true,
}));

const availableColumns = computed(() => defaultColumns.filter(col => columnSettingsGates.value[col.field] ?? true));

// The column selector should only offer columns this install's grid can actually show. Gated-out
// columns are kept in the underlying config (merged back on update) so their order and visibility
// survive if the Hal option is later enabled.
const selectorColumnOrder = computed(() => columnOrder.value.filter(col => isColumnAvailable(col.field)));
const selectorVisibleColumns = computed(() => visibleColumns.value.filter(field => isColumnAvailable(field)));

const monthlyTotal = computed(() => filteredRows.value.reduce((sum, row) => sum + (Number(row.monthly) || 0), 0));
const grandTotal = computed(() => filteredRows.value.reduce((sum, row) => sum + (Number(row.total) || 0), 0));

const aeNames = computed(() => {
  const names = [...new Set(expirations.value?.map(expiration => expiration.aeName))]
    .sort((a, b) => a.localeCompare(b));
  const currentUserName = settings.value?.currentUserName;

  if (currentUserName && names.includes(currentUserName)) {
    return [currentUserName, ...names.filter(name => name !== currentUserName)];
  }

  return names;
});
const zones = computed(() => [...new Set(expirations.value?.map(expiration => expiration.zone))]
  .sort((a, b) => a - b)
  .map(zone => ({ label: String(zone), value: zone })));
const offices = computed(() => [...new Set(expirations.value?.map(expiration => expiration.office))]);
const statuses = computed(() => [...new Set(expirations.value?.map(expiration => expiration.status))]);

const resetTableFilters = () => tableFilters.value = { ...defaultFilters };

const hideTableFilters = () => {
  const activeFilter = JSON.stringify(tableFilters.value);
  const defaultFilter = JSON.stringify(defaultFilters);

  if (activeFilter !== defaultFilter) {
    previousTableFilters.value = { ...tableFilters.value };
  }
  tableFilters.value = undefined;
};

const showTableFilters = () => {
  if (previousTableFilters.value) {
    tableFilters.value = { ...previousTableFilters.value };
    previousTableFilters.value = undefined;
  } else {
    resetTableFilters();
  }
};

const parseNumericFilterInput = (value: string | null): number | null => {
  if (value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const numericFilterValue = (filterModel: { value: unknown }): string | null => {
  const { value } = filterModel;
  return Array.isArray(value) || value === null || value === undefined ? null : String(value);
};

const numericBetweenValue = (filterModel: { value: unknown }, index: 0 | 1): string | null => {
  const bounds = filterModel.value;
  const bound = Array.isArray(bounds) ? bounds[index] : null;
  return bound === null || bound === undefined ? null : String(bound);
};

const onNumericFilterInput = (filterModel: { value: unknown }, value: string | null, filterCallback: () => void) => {
  filterModel.value = parseNumericFilterInput(value);
  filterCallback();
};

const onNumericBetweenFilterInput = (
  filterModel: { value: unknown },
  index: 0 | 1,
  value: string | null,
  filterCallback: () => void,
) => {
  const bounds = Array.isArray(filterModel.value) ? [...filterModel.value] : [null, null];
  bounds[index] = parseNumericFilterInput(value);
  filterModel.value = bounds;
  filterCallback();
};

const dateFilterValue = (filterModel: { value: unknown }): string | null =>
  (typeof filterModel.value === 'string' ? filterModel.value : null);

const dateBetweenValue = (filterModel: { value: unknown }, index: 0 | 1): string | null => {
  const bounds = filterModel.value;
  const bound = Array.isArray(bounds) ? bounds[index] : null;
  return typeof bound === 'string' ? bound : null;
};

const onDateFilterInput = (filterModel: { value: unknown }, value: string | null, filterCallback: () => void) => {
  filterModel.value = value || null;
  filterCallback();
};

const onDateBetweenFilterInput = (
  filterModel: { value: unknown },
  index: 0 | 1,
  value: string | null,
  filterCallback: () => void,
) => {
  const bounds = Array.isArray(filterModel.value) ? [...filterModel.value] : [null, null];
  bounds[index] = value || null;
  filterModel.value = bounds;
  filterCallback();
};

const asFilterMetaData = (entry?: DataTableFilterMeta[string]): DataTableFilterMetaData | undefined =>
  (entry && typeof entry === 'object' && 'matchMode' in entry ? entry : undefined);

// Switching a numeric or date filter's operator (e.g. Between -> Equals) leaves the previous value
// in place with a mismatched shape (array vs scalar), which would otherwise hide every row until
// the user retypes a value. Reset the value whenever the operator changes.
watch(
  () => multiModeFilterFields.map(field => asFilterMetaData(tableFilters.value?.[field])?.matchMode),
  (current, previous) => {
    if (!previous) return;
    multiModeFilterFields.forEach((field, index) => {
      if (current[index] === previous[index]) return;
      const entry = asFilterMetaData(tableFilters.value?.[field]);
      if (entry) entry.value = null;
    });
  },
);

const isColumnShown = (field: string) => visibleColumns.value.includes(field) && (columnSettingsGates.value[field] ?? true);

const isColumnAvailable = (field: string) => columnSettingsGates.value[field] ?? true;

const handleSelectorOrderChange = (updatedColumns: ColumnConfig[]) => {
  const gatedOutColumns = columnOrder.value.filter(col => !isColumnAvailable(col.field));
  handleColumnOrderChange([...updatedColumns, ...gatedOutColumns]);
};

const handleSelectorVisibleChange = (updatedFields: string[]) => {
  const gatedOutFields = visibleColumns.value.filter(field => !isColumnAvailable(field));
  handleVisibleColumnChange([...updatedFields, ...gatedOutFields]);
};

watch(() => expirations.value, rows => {
  filteredRows.value = rows ?? [];
  filteredExpirationLength.value = filteredRows.value.length;
}, { immediate: true, deep: true });

const onFilterExpirations = (e: DataTableFilterEvent) => {
  filteredRows.value = (e.filteredValue ?? []) as ExpirationRow[];
  filteredExpirationLength.value = filteredRows.value.length;
};

const onShowFiltersChange = (value: boolean) => {
  setStoredItem({ key: showFiltersKey, value });

  if (!value) {
    filteredRows.value = expirations.value ?? [];
    filteredExpirationLength.value = filteredRows.value.length;
    hideTableFilters();
  } else {
    showTableFilters();
  }
};

const loadExpirationsHome = async (ignoreLoadStatus: boolean = false) => {
  if (isLoading.value) return;
  isLoading.value = true;

  if (expirations.value.length !== 0 && !ignoreLoadStatus) {
    isLoading.value = false;
    return;
  }

  const data = await filterExpirations(currentState.value);
  if (data) {
    expirations.value = data.expirations;
    settings.value = data.settings;
    filteredExpirationLength.value = data.expirations.length;
  }
  isLoading.value = false;
};

const handleStateChange = () => {
  loadExpirationsHome(true);
};

const rowClass = (rowData: ExpirationRow) => (rowData.urgency === 'complete' ? 'opacity-60' : '');

const rowStyle = (rowData: ExpirationRow) => {
  const color = settings.value?.rowColors?.[rowData.urgency]?.rowColor;
  return color ? { backgroundColor: color } : undefined;
};

const pillStyle = (rowData: ExpirationRow) => {
  const color = settings.value?.rowColors?.[rowData.urgency]?.pillColor;
  return color ? { backgroundColor: color, borderColor: color, color: '#fff' } : undefined;
};

const onRowClick = (e: DataTableRowClickEvent) => {
  clickTimeout.value = window.setTimeout(() => {
    clickTimeout.value = undefined;
    const row = e.data as ExpirationRow;

    if (row.opportunityId && renewalStatuses.includes(row.status)) {
      openOpportunityFlyout(row.opportunityId);
      return;
    }

    selectedExpiration.value = row;
    showActionDialog.value = true;
  }, delay);
};

const closeActionDialog = () => {
  showActionDialog.value = false;
};

const openOpportunityFlyout = async (opportunityId: number) => {
  await loadOpportunityOptions();
  await router.push({ path: `/crm/expirations/detail/opportunity/${opportunityId}` });
};

const onRenew = async () => {
  const row = selectedExpiration.value;
  if (!row) return;

  const result = await createRenewal(row);
  if (!result) return;

  closeActionDialog();
  await openOpportunityFlyout(result.opportunityId);
};

const onNotRenew = async (reason: string) => {
  const row = selectedExpiration.value;
  if (!row) return;

  const success = await setNotRenewing(row, reason);
  if (success) {
    closeActionDialog();
  }
};

const onIgnoreSubcontract = async () => {
  const row = selectedExpiration.value;
  if (!row) return;

  const success = await ignoreSubcontract(row);
  if (success) {
    closeActionDialog();
  }
};

const startEditingNotes = (rowData: ExpirationRow) => {
  clearTimeout(clickTimeout.value);
  clickTimeout.value = undefined;
  editingNotesKey.value = rowData.rowKey;
  tempNotes = rowData.notes;
};

const onNotesCommit = async (rowData: ExpirationRow) => {
  if (editingNotesKey.value === null) return;
  editingNotesKey.value = null;

  if (tempNotes === rowData.notes) return;

  const success = await updateNotes(rowData, rowData.notes);
  if (!success) {
    rowData.notes = tempNotes;
  }
};

const booleanExportFields = ['firstRight', 'isSubcontract'];

const exportCell = ({ data, field }: DataTableExportFunctionOptions) => {
  const value = booleanExportFields.includes(field) ? (data ? 'Yes' : 'No') : String(data);
  return value.replace(/"/g, '""');
};

const exportCSV = () => {
  dt.value.exportCSV();
};

const reloadPage = () => loadExpirationsHome(true);

onMounted(() => {
  loadExpirationsHome();
  window.addEventListener(CustomEventNames.crm.expirations.reloadExpirationsHome, reloadPage);
});

onBeforeUnmount(() => window.removeEventListener(CustomEventNames.crm.expirations.reloadExpirationsHome, reloadPage));
</script>
<template>
  <AppContainer>
    <FormSkeleton v-if="isLoading" />
    <template v-else>
      <div class="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center gap-2">
          <Avatar
            size="normal"
            shape="circle"
            style="background-color: var(--p-orange-100);"
          >
            <template #icon>
              <CalendarTimesIcon color="var(--p-orange-500)" />
            </template>
          </Avatar>
          <h1 class="text-lg">
            Expirations
          </h1>
          <Badge :value="filteredExpirationLength" size="large" severity="secondary" />
        </div>
        <div class="flex flex-col gap-4 sm:flex-row sm:gap-4">
          <Button
            v-tooltip.left="'Export'"
            severity="secondary"
            @click="exportCSV()"
          >
            <FileExportIcon />
            <span style="font-weight:700;">
              Export
            </span>
          </Button>
        </div>
      </div>
      <div class="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center justify-center gap-2">
          <span class="text-center">Filters</span>
          <ToggleSwitch v-model="showFilters" @update:model-value="val => onShowFiltersChange(val)" />
          <Button
            v-tooltip.left="'Clear Filters'"
            size="small"
            outlined
            severity="secondary"
            class="flex items-center"
            :disabled="!(showFilters && isFilterChanged)"
            @click="resetTableFilters"
          >
            <FilterSlashIcon />
            <span class="font-bold">Clear Filters</span>
          </Button>
        </div>
        <div class="flex items-center justify-center gap-2 flex-nowrap">
          <ColumnDisplaySelector
            :default-table-columns="availableColumns"
            :default-visible-columns="availableColumns.filter(col => col.visible).map(col => col.field)"
            :table-columns="selectorColumnOrder"
            :visible-columns="selectorVisibleColumns"
            storage-prefix="crm:expirations"
            :data-table="dt"
            @update:visible-columns="handleSelectorVisibleChange"
            @update:table-columns="handleSelectorOrderChange"
          />
          <Select
            v-model="currentState"
            fluid
            :options="availableStates"
            class="md:w-14rem"
            @change="handleStateChange"
          >
            <template #value="slotProps">
              <div v-if="slotProps.value" class="flex items-center font-bold">
                <CircleFillIcon v-if="slotProps.value === 'All'" class="mr-2 bg-gray-200 rounded-full" color="var(--tw-text-gray-500)" />
                <CircleFillIcon v-else class="mr-2 bg-green-200 rounded-full" color="var(--tw-text-green-500)" />
                <div>{{ slotProps.value }}</div>
              </div>
            </template>
            <template #option="slotProps">
              <div class="flex items-center">
                <CircleFillIcon v-if="slotProps.option == 'All'" class="mr-2 bg-gray-200 rounded-full" color="var(--tw-text-gray-500)" />
                <CircleFillIcon v-else class="mr-2 bg-green-200 rounded-full" color="var(--tw-text-green-500)" />
                <div>{{ slotProps.option }}</div>
              </div>
            </template>
          </Select>
        </div>
      </div>
      <HeightOptimizer>
        <template #default="{availableHeight}">
          <DataTable
            ref="dt"
            v-model:filters="tableFilters"
            :value="expirations"
            :filter-display="showFilters ? 'row' : null"
            :virtual-scroller-options="{itemSize: 50}"
            scrollable
            data-key="rowKey"
            table-style="min-width: 50rem;"
            :scroll-height="availableHeight"
            reorderable-columns
            resizable-columns
            :row-class="rowClass"
            :row-style="rowStyle"
            :export-function="exportCell"
            @row-click="onRowClick"
            @filter="onFilterExpirations"
          >
            <!-- Auto layout lets nowrap content widen a column past a lone `width`; pairing it with `max-width` clamps it (the same trick PrimeVue's column resize uses). -->
            <Column
              field="accountName"
              :header="defaultColumns.find(col => col.field === 'accountName').header"
              sortable
              :hidden="!isColumnShown('accountName')"
              :reorderable-column="false"
              style="width: 200px; max-width: 200px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Accounts"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
              <template #body="slotProps">
                <span class="block overflow-hidden whitespace-nowrap font-semibold">{{ slotProps.data.accountName }}</span>
              </template>
              <template #footer>
                <span class="font-semibold">Total</span>
              </template>
            </Column>
            <Column
              field="contractId"
              :header="defaultColumns.find(col => col.field === 'contractId').header"
              sortable
              :hidden="!isColumnShown('contractId')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Contracts"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="endDate"
              :header="defaultColumns.find(col => col.field === 'endDate').header"
              sortable
              :hidden="!isColumnShown('endDate')"
              :reorderable-column="false"
              :filter-match-mode-options="dateFilterMatchModeOptions"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <div v-show="showFilters" class="flex gap-1">
                  <template v-if="filterModel.matchMode === FilterMatchMode.BETWEEN">
                    <InputText
                      type="date"
                      :model-value="dateBetweenValue(filterModel, 0)"
                      class="p-column-filter"
                      style="min-width: 8rem; max-width: 8rem"
                      @update:model-value="value => onDateBetweenFilterInput(filterModel, 0, value, filterCallback)"
                    />
                    <InputText
                      type="date"
                      :model-value="dateBetweenValue(filterModel, 1)"
                      class="p-column-filter"
                      style="min-width: 8rem; max-width: 8rem"
                      @update:model-value="value => onDateBetweenFilterInput(filterModel, 1, value, filterCallback)"
                    />
                  </template>
                  <InputText
                    v-else-if="filterModel.matchMode === FilterMatchMode.CONTAINS"
                    v-model="filterModel.value"
                    type="text"
                    class="p-column-filter"
                    placeholder="End Dates"
                    style="min-width: 8rem; max-width: 8rem"
                    @input="filterCallback()"
                  />
                  <InputText
                    v-else
                    type="date"
                    :model-value="dateFilterValue(filterModel)"
                    class="p-column-filter"
                    style="min-width: 8rem; max-width: 8rem"
                    @update:model-value="value => onDateFilterInput(filterModel, value, filterCallback)"
                  />
                </div>
              </template>
              <template #body="slotProps">
                {{ slotProps.data.isMonthToMonth ? `${slotProps.data.endDate} (M)` : slotProps.data.endDate }}
              </template>
            </Column>
            <Column
              field="zone"
              :header="defaultColumns.find(col => col.field === 'zone').header"
              sortable
              :hidden="!isColumnShown('zone')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                  v-show="showFilters"
                  v-model="filterModel.value"
                  :options="zones"
                  option-label="label"
                  option-value="value"
                  filter
                  placeholder="Zones"
                  class="p-column-filter"
                  style="min-width: 6rem; max-width: 8rem"
                  :show-clear="true"
                  @change="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="periods"
              :header="defaultColumns.find(col => col.field === 'periods').header"
              sortable
              :hidden="!isColumnShown('periods')"
              :reorderable-column="false"
              :filter-match-mode-options="numericFilterMatchModeOptions"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <div v-show="showFilters" class="flex gap-1">
                  <template v-if="filterModel.matchMode === FilterMatchMode.BETWEEN">
                    <InputText
                      :model-value="numericBetweenValue(filterModel, 0)"
                      type="text"
                      inputmode="numeric"
                      class="p-column-filter"
                      placeholder="Min"
                      style="min-width: 3.5rem; max-width: 3.5rem"
                      @update:model-value="value => onNumericBetweenFilterInput(filterModel, 0, value, filterCallback)"
                    />
                    <InputText
                      :model-value="numericBetweenValue(filterModel, 1)"
                      type="text"
                      inputmode="numeric"
                      class="p-column-filter"
                      placeholder="Max"
                      style="min-width: 3.5rem; max-width: 3.5rem"
                      @update:model-value="value => onNumericBetweenFilterInput(filterModel, 1, value, filterCallback)"
                    />
                  </template>
                  <InputText
                    v-else
                    :model-value="numericFilterValue(filterModel)"
                    type="text"
                    inputmode="numeric"
                    class="p-column-filter"
                    placeholder="Periods"
                    style="min-width: 4rem; max-width: 4rem"
                    @update:model-value="value => onNumericFilterInput(filterModel, value, filterCallback)"
                  />
                </div>
              </template>
            </Column>
            <Column
              field="monthly"
              :header="defaultColumns.find(col => col.field === 'monthly').header"
              sortable
              :hidden="!isColumnShown('monthly')"
              :reorderable-column="false"
              :filter-match-mode-options="numericFilterMatchModeOptions"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <div v-show="showFilters" class="flex gap-1">
                  <template v-if="filterModel.matchMode === FilterMatchMode.BETWEEN">
                    <InputText
                      :model-value="numericBetweenValue(filterModel, 0)"
                      type="text"
                      inputmode="numeric"
                      class="p-column-filter"
                      placeholder="Min"
                      style="min-width: 4rem; max-width: 4rem"
                      @update:model-value="value => onNumericBetweenFilterInput(filterModel, 0, value, filterCallback)"
                    />
                    <InputText
                      :model-value="numericBetweenValue(filterModel, 1)"
                      type="text"
                      inputmode="numeric"
                      class="p-column-filter"
                      placeholder="Max"
                      style="min-width: 4rem; max-width: 4rem"
                      @update:model-value="value => onNumericBetweenFilterInput(filterModel, 1, value, filterCallback)"
                    />
                  </template>
                  <InputText
                    v-else
                    :model-value="numericFilterValue(filterModel)"
                    type="text"
                    inputmode="numeric"
                    class="p-column-filter"
                    placeholder="Monthly"
                    style="min-width: 6rem; max-width: 6rem"
                    @update:model-value="value => onNumericFilterInput(filterModel, value, filterCallback)"
                  />
                </div>
              </template>
              <template #body="slotProps">
                {{ formatAsCurrency(slotProps.data.monthly) }}
              </template>
              <template #footer>
                <span class="font-semibold">{{ formatAsCurrency(monthlyTotal) }}</span>
              </template>
            </Column>
            <Column
              field="total"
              :header="defaultColumns.find(col => col.field === 'total').header"
              sortable
              :hidden="!isColumnShown('total')"
              :reorderable-column="false"
              :filter-match-mode-options="numericFilterMatchModeOptions"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <div v-show="showFilters" class="flex gap-1">
                  <template v-if="filterModel.matchMode === FilterMatchMode.BETWEEN">
                    <InputText
                      :model-value="numericBetweenValue(filterModel, 0)"
                      type="text"
                      inputmode="numeric"
                      class="p-column-filter"
                      placeholder="Min"
                      style="min-width: 4rem; max-width: 4rem"
                      @update:model-value="value => onNumericBetweenFilterInput(filterModel, 0, value, filterCallback)"
                    />
                    <InputText
                      :model-value="numericBetweenValue(filterModel, 1)"
                      type="text"
                      inputmode="numeric"
                      class="p-column-filter"
                      placeholder="Max"
                      style="min-width: 4rem; max-width: 4rem"
                      @update:model-value="value => onNumericBetweenFilterInput(filterModel, 1, value, filterCallback)"
                    />
                  </template>
                  <InputText
                    v-else
                    :model-value="numericFilterValue(filterModel)"
                    type="text"
                    inputmode="numeric"
                    class="p-column-filter"
                    placeholder="Totals"
                    style="min-width: 6rem; max-width: 6rem"
                    @update:model-value="value => onNumericFilterInput(filterModel, value, filterCallback)"
                  />
                </div>
              </template>
              <template #body="slotProps">
                {{ formatAsCurrency(slotProps.data.total) }}
              </template>
              <template #footer>
                <span class="font-semibold">{{ formatAsCurrency(grandTotal) }}</span>
              </template>
            </Column>
            <Column
              field="aeName"
              :header="defaultColumns.find(col => col.field === 'aeName').header"
              sortable
              :hidden="!isColumnShown('aeName')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                  v-show="showFilters"
                  v-model="filterModel.value"
                  :options="aeNames"
                  filter
                  placeholder="AEs"
                  class="p-column-filter"
                  style="min-width: 8rem; max-width: 12rem"
                  :show-clear="true"
                  @change="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="firstRight"
              :header="defaultColumns.find(col => col.field === 'firstRight').header"
              sortable
              :hidden="!isColumnShown('firstRight')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                  v-show="showFilters"
                  v-model="filterModel.value"
                  :options="firstRightOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="First Right"
                  class="p-column-filter"
                  style="min-width: 8rem; max-width: 8rem"
                  :show-clear="true"
                  @change="filterCallback()"
                />
              </template>
              <template #body="slotProps">
                {{ slotProps.data.firstRight ? 'Yes' : 'No' }}
              </template>
            </Column>
            <Column
              field="office"
              :header="defaultColumns.find(col => col.field === 'office').header"
              sortable
              :hidden="!isColumnShown('office')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                  v-show="showFilters"
                  v-model="filterModel.value"
                  :options="offices"
                  filter
                  placeholder="Offices"
                  class="p-column-filter"
                  style="min-width: 8rem; max-width: 12rem"
                  :show-clear="true"
                  @change="filterCallback()"
                />
              </template>
            </Column>
            <Column
              field="boards"
              :header="defaultColumns.find(col => col.field === 'boards').header"
              :hidden="!isColumnShown('boards')"
              :reorderable-column="false"
              style="width: 160px; max-width: 160px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Boards"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
              <template #body="slotProps">
                <span v-tooltip.top="slotProps.data.boards.join('; ')" class="block overflow-hidden whitespace-nowrap">{{ slotProps.data.boards.join('; ') }}</span>
              </template>
            </Column>
            <Column
              field="status"
              :header="defaultColumns.find(col => col.field === 'status').header"
              sortable
              :hidden="!isColumnShown('status')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                  v-show="showFilters"
                  v-model="filterModel.value"
                  :options="statuses"
                  filter
                  placeholder="Statuses"
                  class="p-column-filter"
                  style="min-width: 8rem; max-width: 12rem"
                  :show-clear="true"
                  @change="filterCallback()"
                />
              </template>
              <template #body="slotProps">
                <Tag
                  :value="slotProps.data.status"
                  :severity="getExpirationStatusSeverity(slotProps.data.status)"
                  :style="pillStyle(slotProps.data)"
                />
              </template>
            </Column>
            <Column
              field="isSubcontract"
              :header="defaultColumns.find(col => col.field === 'isSubcontract').header"
              sortable
              :hidden="!isColumnShown('isSubcontract')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                  v-show="showFilters"
                  v-model="filterModel.value"
                  :options="firstRightOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="Subcontract"
                  class="p-column-filter"
                  style="min-width: 8rem; max-width: 8rem"
                  :show-clear="true"
                  @change="filterCallback()"
                />
              </template>
              <template #body="slotProps">
                {{ slotProps.data.isSubcontract ? 'Yes' : 'No' }}
              </template>
            </Column>
            <Column
              field="notes"
              :header="defaultColumns.find(col => col.field === 'notes').header"
              sortable
              :hidden="!isColumnShown('notes')"
              :reorderable-column="false"
              style="width: 0px"
            >
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-show="showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  placeholder="Notes"
                  style="min-width: 8rem; max-width: 8rem"
                  @input="filterCallback()"
                />
              </template>
              <template #body="slotProps">
                <div v-if="editingNotesKey === slotProps.data.rowKey" class="flex items-center gap-2" @click.stop>
                  <InputText
                    v-model="slotProps.data.notes"
                    autofocus
                    class="min-w-0 flex-1"
                    maxlength="255"
                    @blur="onNotesCommit(slotProps.data)"
                    @keyup.enter="onNotesCommit(slotProps.data)"
                  />
                  <small
                    class="shrink-0 whitespace-nowrap"
                    :class="(slotProps.data.notes?.length ?? 0) >= 255 ? 'text-red-600' : 'text-gray-400'"
                  >
                    {{ slotProps.data.notes?.length ?? 0 }}/255
                  </small>
                </div>
                <div v-else @click.stop @dblclick.stop="startEditingNotes(slotProps.data)">
                  {{ slotProps.data.notes }}&nbsp;
                </div>
              </template>
            </Column>
          </DataTable>
        </template>
      </HeightOptimizer>
    </template>
    <ExpirationActionDialog
      :visible="showActionDialog"
      :row="selectedExpiration"
      :reasons="settings?.notRenewReasons ?? []"
      :can-act="settings?.canAct ?? false"
      :is-acting="isActing"
      @close="closeActionDialog"
      @on:renew="onRenew"
      @on:not-renew="onNotRenew"
      @on:ignore-subcontract="onIgnoreSubcontract"
    />
  </AppContainer>
  <RouterView v-if="isKeepAliveActive" />
</template>

<style scoped>
:deep(.p-datatable-header) {
    background: none;
}

.p-button-secondary {
  background-color: lightgray;
  background: var(--white);
  border: 1px solid var(--black-16);
  color: var(--black-80);
}

.p-badge-secondary {
  background-color: lightgray;
  background: var(--white);
  border: 1px solid var(--black-16);
  color: var(--black-80);
}
</style>
